import { useState } from 'react';

import { usePaymentContext } from 'features/payment';
import {
  CardTypes,
  IApplePayDetails,
  IGooglePayDetails,
  IPaymentMethod,
  IReloadPrepaidCard,
} from 'features/payment/types';
import {
  getFraudPreventionValues,
  isApplePay,
  isGooglePay,
  splitExpiry,
} from 'features/payment/utils';

import { IServerOrder } from '@rbi-ctg/menu';
import { CartPaymentCardType } from 'generated/graphql-gateway';
import { ICommitOrderInput } from 'hooks/commit-order/types';
import useEffectOnce from 'hooks/use-effect-once';
import { getNonce } from 'remote/api/first-data';
import { UserDetails } from 'state/auth/hooks/use-current-user';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
import { IUseCartTip } from 'state/order/hooks/use-cart-tip';
import useRewards from 'state/rewards/use-rewards';
import { getConfigValue } from 'utils/environment';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import logger from 'utils/logger';

import { testCardPaymentState } from '../initial-state';

enum PaymentMode {
  ADDING_NEW_CARD,
  SELECTING_EXISTING_CARD,
}

interface IPrepaidMethodReload {
  commitOrder: (options: ICommitOrderInput) => void;
  handleCommitError: (error: any) => void;
  allPaymentMethods: IPaymentMethod[];
  user: UserDetails | null;
  serverOrder: IServerOrder | null;
  selectedPaymentMethod: IPaymentMethod | undefined;
  shouldCommitOrder: boolean;
  storeEmail: string;
  tipAmount: IUseCartTip['tipAmount'];
}

const usePrepaidMethodReload = ({
  commitOrder,
  handleCommitError,
  allPaymentMethods,
  user,
  serverOrder,
  selectedPaymentMethod,
  shouldCommitOrder,
  storeEmail,
  tipAmount,
}: IPrepaidMethodReload) => {
  const {
    addPaymentMethod,
    checkoutPaymentMethodId,
    getBalanceFromPaymentMethods,
    getEncryptionDetails,
    getPrepaidPaymentMethod,
    paymentMethods,
    prepaidReloadPaymentMethodId,
    reloadPrepaidCard,
    transformPaymentValues,
    reloadPaymentValues,
    setReloadPaymentValues,
  } = usePaymentContext();
  const autoFill = useFlag(LaunchDarklyFlag.AUTO_FILL_TEST_CARD);
  const billingCountry = (user?.details?.isoCountryCode as ISOs) || ISOs.USA;
  const [needReloadAgain, setNeedReloadAgain] = useState(false);
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;

  const fdUrl = getConfigValue({ key: 'firstData', defaultValue: {} })?.baseUrl;

  const enableGiftCard = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD);
  const onlySendPostalCode = useFlag(LaunchDarklyFlag.SEND_POSTAL_CODE_ONLY_FOR_FIRST_DATA_PAYMENT);

  const prepaidPaymentMethod = getPrepaidPaymentMethod();
  const accountIdentifier =
    prepaidPaymentMethod?.accountIdentifier ?? prepaidPaymentMethod?.fdAccountId ?? '';
  const prepaidPaymentMethodSelected =
    !!accountIdentifier && checkoutPaymentMethodId === accountIdentifier;

  const { reloadAmount } = useRewards({
    prepaidCard: prepaidPaymentMethod,
    serverOrder,
    tipAmount,
    user,
  });

  // Autofill CC information
  useEffectOnce(() => {
    if (autoFill) {
      setReloadPaymentValues(testCardPaymentState({ paymentFieldVariations }));
    }
  });

  const showReloadPaymentMethods =
    enableGiftCard && prepaidPaymentMethodSelected && reloadAmount > 0;

  const allowedPaymentMethods = paymentMethods.filter(
    method => !method.prepaid && !method.cash && !method.paypal && !method.venmo
  );
  const mode: PaymentMode =
    allowedPaymentMethods.length > 0
      ? PaymentMode.SELECTING_EXISTING_CARD
      : PaymentMode.ADDING_NEW_CARD;

  const reloadWithApplePay = showReloadPaymentMethods && isApplePay(prepaidReloadPaymentMethodId);

  const reloadWithGooglePay = showReloadPaymentMethods && isGooglePay(prepaidReloadPaymentMethodId);

  const maybeAddNewCard = (): Promise<string | void> => {
    if (
      !reloadWithApplePay &&
      !reloadWithGooglePay &&
      reloadPaymentValues.saveCard &&
      mode === PaymentMode.ADDING_NEW_CARD
    ) {
      return addPaymentMethod(
        transformPaymentValues({
          paymentValues: reloadPaymentValues,
          paymentFieldVariations,
        })
      );
    }
    return Promise.resolve();
  };

  const getPrepaidBalance = (methods: IPaymentMethod[]) => {
    const prepaidAccount = methods.find((method: IPaymentMethod) => Boolean(method.prepaid));
    return getBalanceFromPaymentMethods(prepaidAccount!);
  };

  const maybeReloadGiftCard = async (
    input?: IApplePayDetails | IGooglePayDetails
  ): Promise<number> => {
    setNeedReloadAgain(false);

    const shouldReloadGiftCard = reloadAmount > 0 && prepaidPaymentMethodSelected;

    if (!shouldReloadGiftCard) {
      return Promise.resolve(getPrepaidBalance(allPaymentMethods));
    }

    // Add the new payment method if is necessary
    const paymentMethodId = (await maybeAddNewCard()) || prepaidReloadPaymentMethodId;

    const prepaidBalance = getPrepaidBalance(allPaymentMethods);
    const { countryCode } = getCountryAndCurrencyCodes(billingCountry);
    const prepaidFdAccountId =
      prepaidPaymentMethod?.accountIdentifier ?? prepaidPaymentMethod?.fdAccountId ?? '';

    let args: IReloadPrepaidCard = {
      countryCode,
      fundValue: reloadAmount,
      prepaidBalance,
      prepaidFdAccountId,
      fdAccountId: paymentMethodId,
    };

    switch (paymentMethodId) {
      case CardTypes.APPLE_PAY:
        args = {
          ...args,
          applePayDetails: input as IApplePayDetails,
        };
        break;
      case CardTypes.GOOGLE_PAY:
        args = {
          ...args,
          googlePayDetails: input as IGooglePayDetails,
        };
        break;
    }

    // return original balance if reload failed
    return reloadPrepaidCard(args).then(updatedBalance => updatedBalance ?? prepaidBalance);
  };

  const buildCommitOrderPayload = async (prepaidBalance: number): Promise<ICommitOrderInput> => {
    if (!serverOrder) {
      return Promise.reject('Required parameters are not defined.');
    }

    if (prepaidBalance < serverOrder.cart.totalCents) {
      setNeedReloadAgain(true);
      return Promise.reject('Insufficient prepaid balance.');
    }

    if (prepaidPaymentMethodSelected) {
      const result: ICommitOrderInput = {
        fdAccountId:
          prepaidPaymentMethod?.accountIdentifier ?? prepaidPaymentMethod?.fdAccountId ?? '',
      };
      return Promise.resolve(result);
    }

    if (reloadWithApplePay || reloadWithGooglePay) {
      const result: ICommitOrderInput = {};
      return Promise.resolve(result);
    }

    if (selectedPaymentMethod) {
      const result: ICommitOrderInput = {
        creditType: selectedPaymentMethod.credit
          ? ((selectedPaymentMethod.credit.cardType as unknown) as CartPaymentCardType)
          : null,
        fdAccountId:
          selectedPaymentMethod.accountIdentifier ?? selectedPaymentMethod.fdAccountId ?? '',
      };
      return Promise.resolve(result);
    }

    // Otherwise we have to jump through hoops to get more values from First Data
    const {
      fdAccessToken,
      fdApiKey,
      fdCustomerId,
      fdPublicKey,
      algorithm,
    } = await getEncryptionDetails();

    const reshapedPaymentValues = transformPaymentValues({
      paymentValues: reloadPaymentValues,
      paymentFieldVariations,
    });
    const nonceResponse = await getNonce(
      reshapedPaymentValues,
      fdPublicKey,
      fdApiKey,
      fdAccessToken,
      // @ts-expect-error TS(2345) FIXME: Argument of type 'string | null | undefined' is no... Remove this comment to see the full error message
      fdCustomerId,
      fdUrl,
      onlySendPostalCode,
      algorithm
    );
    const nonce = await nonceResponse.json();

    const fraudPreventionValues = getFraudPreventionValues(reloadPaymentValues);
    const { expiryMonth, expiryYear } = splitExpiry(fraudPreventionValues.expiry ?? '');

    const result: ICommitOrderInput = {
      creditType: reloadPaymentValues.cardType as CartPaymentCardType,
      payment: {
        billingAddress: fraudPreventionValues.billingAddress,
        ccMetadata: {
          ccBin: fraudPreventionValues.ccBin,
          ccExpiryMonth: expiryMonth,
          ccExpiryYear: expiryYear,
          ccLast4: fraudPreventionValues.ccLast4,
        },
        firstDataInput: {
          accountIdentifier: reloadPaymentValues.saveCard ? accountIdentifier : undefined,
          fdAccessToken: reloadPaymentValues.saveCard ? undefined : fdAccessToken,
          fdNonce: reloadPaymentValues.saveCard ? undefined : nonce.token.tokenId,
        },
        fullName: fraudPreventionValues.fullName,
      },
    };
    return result;
  };

  const reloadGiftCardAndCheckout = async (data?: IApplePayDetails | IGooglePayDetails) => {
    // should never happen, since user cannot submit without
    // successfully pricing an order
    if (!serverOrder) {
      throw new Error('unreachable');
    }

    try {
      // reloads prepaid if necessary
      const currentPrepaidBalance = await maybeReloadGiftCard(data);

      if (shouldCommitOrder) {
        // commit order if prepaid balance is sufficient
        const commitOrderPayload = await buildCommitOrderPayload(currentPrepaidBalance);
        await commitOrder(commitOrderPayload);
      }
    } catch (e) {
      logger.error(`Error reloading giftcard: ${e}`);
      handleCommitError(e);
    }
  };

  return {
    reloadAddPaymentMethod: mode === PaymentMode.ADDING_NEW_CARD,
    reloadAmount,
    reloadGiftCardAndCheckout,
    reloadPaymentValues,
    needReloadAgain,
    showReloadPaymentMethods,
    setReloadPaymentValues,
  };
};

export default usePrepaidMethodReload;
